import React from "react";
import { Link, useParams } from "react-router-dom";

import { InvestmentsWorkspaceShell } from "@features/investments/components/workspace-shell";
import { InvestmentsLoadingScreen, InvestmentsPageState } from "@features/investments/components/page-state";
import {
  GLOSSARY_CATEGORIES,
  INVESTMENTS_GLOSSARY_ENTRIES,
  findGlossaryEntryBySlug,
  glossaryRoute,
  type GlossaryCategoryKey,
  type GlossaryEntry,
} from "@features/investments/glossary/registry";
import { useInvestmentsAccessQuery } from "@features/investments/queries";
import "@views/investments/index.scss";

type CategoryFilter = GlossaryCategoryKey | "all";

const GlossaryScreen = (): JSX.Element => {
  const accessQuery = useInvestmentsAccessQuery();
  const params = useParams();
  const selectedSlug = params.slug || "";
  const selectedEntry = findGlossaryEntryBySlug(selectedSlug);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState<CategoryFilter>("all");

  const filteredEntries = React.useMemo(() => {
    const query = normalizeGlossaryQuery(searchTerm);
    return INVESTMENTS_GLOSSARY_ENTRIES.filter((entry) => {
      if (categoryFilter !== "all" && entry.category !== categoryFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      return glossarySearchText(entry).includes(query);
    });
  }, [categoryFilter, searchTerm]);

  if (accessQuery.isLoading) {
    return <InvestmentsLoadingScreen message="Loading glossary..." />;
  }

  if (accessQuery.error) {
    return <InvestmentsPageState message={`Failed to resolve investments access: ${accessQuery.error.message}`} />;
  }

  return (
    <InvestmentsWorkspaceShell
      title="Glossary"
      description="Definitions, formulas, and house rules for the Investments workflow."
      headerMeta={<div className="dashboard-generated-at">Reference index</div>}
    >
      <section className="workflow-section">
        <div className="workflow-section-header">
          <div>
            <h2>Investments Glossary</h2>
            <p>Use this as the shared reference for rates, valuation, ETF, cash-flow, quality, and workflow terms.</p>
          </div>
          <div className="workflow-section-meta">{filteredEntries.length} of {INVESTMENTS_GLOSSARY_ENTRIES.length} terms</div>
        </div>

        <div className="reference-tab-strip" role="tablist" aria-label="Reference sections">
          <span className="reference-tab-link active">Glossary</span>
          <Link className="reference-tab-link" to="/investments/reference/method">Method</Link>
          <Link className="reference-tab-link" to="/investments/reference/help">Help</Link>
        </div>

        <div className="glossary-layout">
          <aside className="investments-card workflow-card glossary-list-panel">
            <div className="glossary-toolbar">
              <label className="glossary-search-field">
                <span>Search</span>
                <input
                  type="text"
                  value={searchTerm}
                  placeholder="Search term, slug, or alias"
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </label>
              <div className="glossary-category-group" aria-label="Glossary categories">
                <button
                  type="button"
                  className={categoryFilter === "all" ? "active" : ""}
                  onClick={() => setCategoryFilter("all")}
                >
                  All
                </button>
                {GLOSSARY_CATEGORIES.map((category) => (
                  <button
                    key={category.key}
                    type="button"
                    className={categoryFilter === category.key ? "active" : ""}
                    onClick={() => setCategoryFilter(category.key)}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredEntries.length === 0 ? (
              <div className="workflow-empty glossary-empty-state">
                No glossary terms match the current search and category filter.
              </div>
            ) : (
              <div className="glossary-result-list" role="list" aria-label="Glossary terms">
                {filteredEntries.map((entry) => (
                  <Link
                    key={entry.slug}
                    className={`glossary-result-link${selectedEntry?.slug === entry.slug ? " active" : ""}`}
                    to={glossaryRoute(entry.slug)}
                  >
                    <div className="glossary-result-top">
                      <strong>{entry.label}</strong>
                      <span>{categoryLabel(entry.category)}</span>
                    </div>
                    <p>{entry.shortDefinition}</p>
                  </Link>
                ))}
              </div>
            )}
          </aside>

          <section className="investments-card workflow-card glossary-detail-panel">
            {selectedSlug && !selectedEntry && <GlossaryMissingEntry slug={selectedSlug} />}
            {!selectedSlug && <GlossaryIntro />}
            {selectedEntry && <GlossaryEntryDetail entry={selectedEntry} />}
          </section>
        </div>
      </section>
    </InvestmentsWorkspaceShell>
  );
};

function GlossaryIntro(): JSX.Element {
  return (
    <div className="glossary-detail-empty">
      <div className="section-eyebrow">Reference</div>
      <h2>Select a term</h2>
      <p>Pick a glossary entry from the list to see the definition, formula, interpretation, house rule, and related terms.</p>
    </div>
  );
}

function GlossaryMissingEntry({ slug }: { slug: string }): JSX.Element {
  return (
    <div className="glossary-detail-empty">
      <div className="section-eyebrow">Not Found</div>
      <h2>Term not found</h2>
      <p>We could not find a glossary entry for <code>{slug}</code>. Try searching or choose another term from the list.</p>
    </div>
  );
}

function GlossaryEntryDetail({ entry }: { entry: GlossaryEntry }): JSX.Element {
  const relatedEntries = React.useMemo(() => {
    const resolved = (entry.related ?? [])
      .map((slug) => findGlossaryEntryBySlug(slug))
      .filter((value): value is GlossaryEntry => value !== null);
    return resolved.filter((value, index) => resolved.findIndex((candidate) => candidate.slug === value.slug) === index);
  }, [entry]);

  return (
    <article className="glossary-entry">
      <div className="glossary-entry-header">
        <div className="section-eyebrow">{categoryLabel(entry.category)}</div>
        <h2>{entry.label}</h2>
        <p className="glossary-entry-short">{entry.shortDefinition}</p>
      </div>

      <div className="glossary-entry-body">
        <GlossarySection title="Definition">
          <p>{entry.explanation}</p>
        </GlossarySection>

        {entry.formula && (
          <GlossarySection title="Formula">
            <pre className="glossary-formula">{entry.formula}</pre>
          </GlossarySection>
        )}

        {entry.interpretation && (
          <GlossarySection title="Interpretation">
            <p>{entry.interpretation}</p>
          </GlossarySection>
        )}

        {entry.houseRule && (
          <GlossarySection title="House Rule">
            <p>{entry.houseRule}</p>
          </GlossarySection>
        )}

        {entry.aliases && entry.aliases.length > 0 && (
          <GlossarySection title="Also Used As">
            <div className="glossary-chip-row">
              {entry.aliases.map((alias) => (
                <span key={alias} className="glossary-chip">{alias}</span>
              ))}
            </div>
          </GlossarySection>
        )}

        {relatedEntries.length > 0 && (
          <GlossarySection title="Related Terms">
            <div className="glossary-related-list">
              {relatedEntries.map((relatedEntry) => (
                <Link key={relatedEntry.slug} className="glossary-related-link" to={glossaryRoute(relatedEntry.slug)}>
                  {relatedEntry.label}
                </Link>
              ))}
            </div>
          </GlossarySection>
        )}
      </div>
    </article>
  );
}

function GlossarySection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="glossary-entry-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function categoryLabel(category: GlossaryCategoryKey): string {
  return GLOSSARY_CATEGORIES.find((value) => value.key === category)?.label ?? category;
}

function glossarySearchText(entry: GlossaryEntry): string {
  return normalizeGlossaryQuery([entry.label, entry.slug, ...(entry.aliases ?? [])].join(" "));
}

function normalizeGlossaryQuery(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

export default GlossaryScreen;
